import { useState, useEffect, useRef } from "react";
import { Droplets, Clock, Plus, Settings, History, Loader2, Trash2, Save, Download, FileSpreadsheet, Upload, MapPin, Calendar, Bell, Send, User, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configuración por defecto
const DEFAULT_FLOW_RATE = 10; // litros/segundo
const M3_POR_HORA = (flowRate) => flowRate * 3.6; // l/s * 3600s / 1000 = m³/hora
// Fórmula oficial La Aldea: 1 azada = 10 l/s durante 12 h = 43.200 s = 432.000 l = 432 m³
const M3_POR_AZADA = 432;

const IrrigationManager = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("register");
  const [loading, setLoading] = useState(false);
  const [controlPoints, setControlPoints] = useState([]);
  const [consumption, setConsumption] = useState(null);
  const [history, setHistory] = useState([]);
  
  // Configuración de cálculo (caudal en l/s). El caudal define cuántas horas = 1 azada
  const [caudalLitrosSegundo, setCaudalLitrosSegundo] = useState(() => {
    const saved = localStorage.getItem('centinela_caudal_ls');
    return saved ? parseFloat(saved) : DEFAULT_FLOW_RATE;
  });
  
  // Direct registration form
  const [selectedPoint, setSelectedPoint] = useState("");
  const [hoursWater, setHoursWater] = useState("");
  const [numAzadas, setNumAzadas] = useState("");
  // Operador se inicializa con el nombre completo del usuario
  const defaultOperator = user?.nombre_completo || user?.username || "";
  const [operator, setOperator] = useState(defaultOperator);
  const [notes, setNotes] = useState("");
  const [registrationDate, setRegistrationDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Programación de tareas
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    point_id: "",
    scheduled_date: new Date().toISOString().split('T')[0],
    scheduled_time: "08:00",
    hours_water: "",
    azadas: "",
    assigned_operator: "",
    notes: ""
  });
  
  // Calcular metros cúbicos (horas × caudal)
  const calcularM3 = (horas) => {
    return (horas * M3_POR_HORA(caudalLitrosSegundo)).toFixed(1);
  };
  
  // Calcular horas desde azadas: horas = azadas × 432 / (caudal × 3.6)
  const azadasToHoras = (azadas) => {
    return ((azadas * M3_POR_AZADA) / M3_POR_HORA(caudalLitrosSegundo)).toFixed(2);
  };
  
  // Calcular azadas desde horas: azadas = m³ / 432
  const horasToAzadas = (horas) => {
    return ((horas * M3_POR_HORA(caudalLitrosSegundo)) / M3_POR_AZADA).toFixed(2);
  };
  
  // Guardar configuración cuando cambia
  useEffect(() => {
    localStorage.setItem('centinela_caudal_ls', caudalLitrosSegundo.toString());
  }, [caudalLitrosSegundo]);
  
  // Sincronizar horas y azadas
  useEffect(() => {
    if (hoursWater && !isNaN(parseFloat(hoursWater))) {
      setNumAzadas(horasToAzadas(parseFloat(hoursWater)));
    }
  }, [hoursWater, caudalLitrosSegundo]);
  
  // Hora de apertura y cierre
  const [openTime, setOpenTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [closeTime, setCloseTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  
  // Calcular azadas automáticamente cuando cambian las horas
  const calculateHoursFromTimes = (open, close) => {
    if (!open || !close) return 0;
    const [openH, openM] = open.split(':').map(Number);
    const [closeH, closeM] = close.split(':').map(Number);
    
    let openMinutes = openH * 60 + openM;
    let closeMinutes = closeH * 60 + closeM;
    
    // Si cierre es menor que apertura, asumimos que pasó la medianoche
    if (closeMinutes < openMinutes) {
      closeMinutes += 24 * 60;
    }
    
    const diffMinutes = closeMinutes - openMinutes;
    const hours = diffMinutes / 60;
    return Math.round(hours * 4) / 4; // Redondear a cuartos de hora
  };
  
  // Actualizar azadas cuando cambian las horas
  useEffect(() => {
    const calculated = calculateHoursFromTimes(openTime, closeTime);
    if (calculated > 0) {
      setHoursWater(calculated.toString());
    }
  }, [openTime, closeTime]);
  
  // Export dates
  const [exportStartDate, setExportStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [exportEndDate, setExportEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [exporting, setExporting] = useState(false);
  
  // New control point form
  const [newPointName, setNewPointName] = useState("");
  const [newPointLocation, setNewPointLocation] = useState("");
  const [newPointFlowRate, setNewPointFlowRate] = useState("10");
  const [showAddPoint, setShowAddPoint] = useState(false);
  
  // Puntos intermedios
  const [intermediatePoints, setIntermediatePoints] = useState([]);
  const [showAddIntermediate, setShowAddIntermediate] = useState(false);
  const [newIntermediate, setNewIntermediate] = useState({
    name: "",
    dam_id: "",
    distance_to_dam_km: "",
    measurement_time: "",
    departure_time: "",
    notes: ""
  });
  
  // Registro de medición en punto intermedio
  const [registeringReading, setRegisteringReading] = useState(null); // ID del punto
  const [intermediateReading, setIntermediateReading] = useState({
    level: "",
    flow_rate: "",
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    operator: defaultOperator,
    notes: ""
  });
  
  // Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pointsRes, consumptionRes, historyRes, intermediateRes, scheduledRes] = await Promise.all([
        axios.get(`${API_URL}/api/irrigation/control-points`),
        axios.get(`${API_URL}/api/irrigation/consumption/summary`),
        axios.get(`${API_URL}/api/irrigation/history?limit=20`),
        axios.get(`${API_URL}/api/irrigation/intermediate-points`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/irrigation/scheduled-tasks`).catch(() => ({ data: [] }))
      ]);
      
      // Si no hay puntos de control, inicializar
      if (!pointsRes.data || pointsRes.data.length === 0) {
        console.log("No hay puntos de control, inicializando...");
        try {
          await axios.post(`${API_URL}/api/irrigation/init-defaults`);
          const newPointsRes = await axios.get(`${API_URL}/api/irrigation/control-points`);
          setControlPoints(newPointsRes.data);
        } catch (initError) {
          console.error("Error initializing defaults:", initError);
        }
      } else {
        setControlPoints(pointsRes.data);
      }
      
      setConsumption(consumptionRes.data);
      setHistory(historyRes.data);
      setIntermediatePoints(intermediateRes.data || []);
      setScheduledTasks(scheduledRes.data || []);
    } catch (error) {
      console.error("Error fetching irrigation data:", error);
      // Initialize defaults if error
      try {
        await axios.post(`${API_URL}/api/irrigation/init-defaults`);
        const pointsRes = await axios.get(`${API_URL}/api/irrigation/control-points`);
        setControlPoints(pointsRes.data);
      } catch (initError) {
        console.error("Error initializing defaults:", initError);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const handleRegisterConsumption = async () => {
    if (!selectedPoint) {
      toast.error("Selecciona un punto de control");
      return;
    }
    if (!hoursWater || parseFloat(hoursWater) <= 0) {
      toast.error("Introduce el tiempo");
      return;
    }
    
    try {
      // Combine date and times
      const openDateTime = `${registrationDate}T${openTime}:00`;
      const closeDateTime = `${registrationDate}T${closeTime}:00`;
      
      await axios.post(`${API_URL}/api/irrigation/register`, {
        control_point_id: selectedPoint,
        hours_water: parseFloat(hoursWater),
        operator,
        notes,
        date: openDateTime,
        opened_at: openDateTime,
        closed_at: closeDateTime
      });
      
      const point = controlPoints.find(p => p.id === selectedPoint);
      toast.success(`Registrado: ${hoursWater}h · ${horasToAzadas(parseFloat(hoursWater))} azadas en ${point?.name || 'punto'} (${openTime} - ${closeTime})`);
      
      // Reset form (mantener operador)
      setSelectedPoint("");
      setHoursWater("");
      setNotes("");
      // Operador se mantiene con el nombre del usuario
      setOperator(defaultOperator);
      // Reset times to current
      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      setOpenTime(currentTime);
      setCloseTime(currentTime);
      
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar consumo");
    }
  };

  const handleAddControlPoint = async () => {
    if (!newPointName) {
      toast.error("Introduce un nombre");
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/irrigation/control-points`, {
        name: newPointName,
        location: newPointLocation,
        flow_rate_ls: parseFloat(newPointFlowRate) || 10
      });
      toast.success("Punto de control creado");
      setNewPointName("");
      setNewPointLocation("");
      setNewPointFlowRate("10");
      setShowAddPoint(false);
      fetchData();
    } catch (error) {
      toast.error("Error al crear punto de control");
    }
  };

  const handleDeleteControlPoint = async (pointId) => {
    if (!confirm("¿Eliminar este punto de control?")) return;
    
    try {
      await axios.delete(`${API_URL}/api/irrigation/control-points/${pointId}`);
      toast.success("Punto de control eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar punto de control");
    }
  };

  // Funciones para puntos intermedios
  const handleAddIntermediatePoint = async () => {
    if (!newIntermediate.name || !newIntermediate.dam_id) {
      toast.error("Introduce nombre y presa");
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/irrigation/intermediate-points`, {
        name: newIntermediate.name,
        dam_id: newIntermediate.dam_id,
        distance_to_dam_km: parseFloat(newIntermediate.distance_to_dam_km) || 0,
        measurement_time: newIntermediate.measurement_time || null,
        departure_time: newIntermediate.departure_time || null,
        notes: newIntermediate.notes
      });
      toast.success("Punto intermedio creado");
      setNewIntermediate({ name: "", dam_id: "", distance_to_dam_km: "", measurement_time: "", departure_time: "", notes: "" });
      setShowAddIntermediate(false);
      fetchData();
    } catch (error) {
      toast.error("Error al crear punto intermedio");
    }
  };

  const handleDeleteIntermediatePoint = async (pointId) => {
    if (!confirm("¿Eliminar este punto intermedio?")) return;
    
    try {
      await axios.delete(`${API_URL}/api/irrigation/intermediate-points/${pointId}`);
      toast.success("Punto intermedio eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar punto intermedio");
    }
  };

  // Funciones para tareas programadas
  const handleScheduleTask = async () => {
    if (!newSchedule.point_id || !newSchedule.scheduled_date || !newSchedule.hours_water) {
      toast.error("Completa punto, fecha y tiempo");
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/irrigation/scheduled-tasks`, {
        control_point_id: newSchedule.point_id,
        scheduled_date: newSchedule.scheduled_date,
        scheduled_time: newSchedule.scheduled_time,
        hours_water: parseFloat(newSchedule.hours_water),
        azadas: parseFloat(newSchedule.azadas) || parseFloat(horasToAzadas(parseFloat(newSchedule.hours_water))),
        m3_estimated: parseFloat(calcularM3(parseFloat(newSchedule.hours_water))),
        assigned_operator: newSchedule.assigned_operator || defaultOperator,
        notes: newSchedule.notes,
        status: "pending"
      });
      
      const point = controlPoints.find(p => p.id === newSchedule.point_id);
      toast.success(`Tarea programada para ${point?.name || 'punto'} el ${newSchedule.scheduled_date}`);
      
      // Enviar notificación al operario si hay email configurado
      if (newSchedule.assigned_operator) {
        try {
          await axios.post(`${API_URL}/api/irrigation/notify-operator`, {
            operator: newSchedule.assigned_operator,
            task: {
              point_name: point?.name,
              date: newSchedule.scheduled_date,
              time: newSchedule.scheduled_time,
              hours: newSchedule.hours_water,
              azadas: newSchedule.azadas || horasToAzadas(parseFloat(newSchedule.hours_water)),
              m3: calcularM3(parseFloat(newSchedule.hours_water)),
              notes: newSchedule.notes
            }
          });
          toast.success(`Notificación enviada a ${newSchedule.assigned_operator}`);
        } catch (notifyError) {
          console.log("No se pudo enviar notificación:", notifyError);
        }
      }
      
      setNewSchedule({
        point_id: "",
        scheduled_date: new Date().toISOString().split('T')[0],
        scheduled_time: "08:00",
        hours_water: "",
        azadas: "",
        assigned_operator: "",
        notes: ""
      });
      setShowScheduleForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al programar tarea");
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.put(`${API_URL}/api/irrigation/scheduled-tasks/${taskId}/complete`);
      toast.success("Tarea marcada como completada");
      fetchData();
    } catch (error) {
      toast.error("Error al completar tarea");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm("¿Eliminar esta tarea programada?")) return;
    
    try {
      await axios.delete(`${API_URL}/api/irrigation/scheduled-tasks/${taskId}`);
      toast.success("Tarea eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar tarea");
    }
  };

  const handleRegisterIntermediateReading = async (pointId) => {
    if (!intermediateReading.level) {
      toast.error("Introduce el nivel medido");
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/irrigation/intermediate-points/${pointId}/reading`, {
        point_id: pointId,
        level: parseFloat(intermediateReading.level),
        flow_rate: intermediateReading.flow_rate ? parseFloat(intermediateReading.flow_rate) : null,
        date: intermediateReading.date,
        time: intermediateReading.time,
        operator: intermediateReading.operator,
        notes: intermediateReading.notes
      });
      toast.success("Medición registrada");
      setRegisteringReading(null);
      setIntermediateReading({
        level: "",
        flow_rate: "",
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        operator: defaultOperator,
        notes: ""
      });
    } catch (error) {
      toast.error("Error al registrar medición");
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/irrigation/export/excel`, {
        params: {
          start_date: exportStartDate,
          end_date: exportEndDate
        }
      });
      
      if (response.data.excel_base64) {
        const link = document.createElement('a');
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.excel_base64}`;
        link.download = response.data.filename || 'consumo_riego.xlsx';
        link.click();
        toast.success(`Exportado: ${response.data.summary.total_hours} horas de tiempo`);
      }
    } catch (error) {
      toast.error("Error al exportar Excel");
      console.error("Export error:", error);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/irrigation/import/template`);
      if (response.data.excel_base64) {
        const link = document.createElement('a');
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.excel_base64}`;
        link.download = response.data.filename || 'plantilla_importacion_riego.xlsx';
        link.click();
        toast.success("Plantilla descargada");
      }
    } catch (error) {
      toast.error("Error al descargar plantilla");
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/irrigation/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Importados ${response.data.imported} registros`);
        fetchData();  // Refresh data
      } else {
        toast.warning("No se importaron registros");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar");
      setImportResult({
        imported: 0,
        errors: [error.response?.data?.detail || "Error desconocido"],
        total_errors: 1
      });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="bg-teal-600/20 border-teal-500/30 text-teal-400 hover:bg-teal-600/30 hover:text-teal-300 h-8 px-3 text-xs"
          data-testid="irrigation-manager-btn"
        >
          <Droplets className="w-4 h-4 mr-1.5" />
          Gestión de Vaciado
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl w-[95vw] max-h-[95vh] overflow-y-auto p-3 sm:p-5">
        <DialogHeader className="pb-2">
          <DialogTitle className="font-heading uppercase tracking-wider text-zinc-100 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 sm:gap-3">
              <a 
                href="https://livewifi.es/centinela" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-shrink-0"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_65caa12a-9cef-4fb1-b65d-555171978c75/artifacts/uc0sdgit_LOGO%20FINAL%20LIVEWIFI%20BLANCO.png" 
                  alt="LIVEWIFI CENTINELA" 
                  className="h-8 sm:h-10 w-auto object-contain"
                />
              </a>
              <div className="flex items-center gap-1.5 sm:gap-2 border-l border-zinc-700 pl-2 sm:pl-3">
                <Droplets className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
                <span className="text-xs sm:text-sm">Gestión de Vaciado</span>
              </div>
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Gestión de vaciado: registro de consumo de agua en azadas, metros cúbicos y tiempo por punto de control.
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
          </div>
        ) : (
          <Tabs defaultValue="register" className="w-full" value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5 bg-zinc-800 h-10 sm:h-12">
              <TabsTrigger value="register" className="data-[state=active]:bg-blue-600 text-[10px] sm:text-sm h-9 sm:h-11 px-1 sm:px-3">
                <Save className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Registrar</span>
              </TabsTrigger>
              <TabsTrigger value="schedule" className="data-[state=active]:bg-purple-600 text-[10px] sm:text-sm h-9 sm:h-11 px-1 sm:px-3">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Programar</span>
              </TabsTrigger>
              <TabsTrigger value="intermediate" className="data-[state=active]:bg-amber-600 text-[10px] sm:text-sm h-9 sm:h-11 px-1 sm:px-3">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Intermedios</span>
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-teal-600 text-[10px] sm:text-sm h-9 sm:h-11 px-1 sm:px-3">
                <Settings className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Config</span>
              </TabsTrigger>
              <TabsTrigger value="consumption" className="data-[state=active]:bg-emerald-600 text-[10px] sm:text-sm h-9 sm:h-11 px-1 sm:px-3">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Consumo</span>
              </TabsTrigger>
            </TabsList>
            
            {/* Register Tab - Direct hours input */}
            <TabsContent value="register" className="space-y-2 mt-2">
              <Card className="bg-zinc-800/50 border-zinc-700 p-3">
                {/* Configuración rápida de cálculo */}
                <div className="flex items-center justify-between mb-3 p-2 bg-zinc-900/50 rounded border border-zinc-700">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-amber-400" />
                      <span className="text-[10px] text-zinc-400">1 azada =</span>
                      <span className="text-[11px] font-mono font-bold text-amber-300" data-testid="irrigation-m3-per-azada">{M3_POR_AZADA} m³</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Droplets className="w-4 h-4 text-blue-400" />
                      <span className="text-[10px] text-zinc-400">Caudal (l/s):</span>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        value={caudalLitrosSegundo}
                        onChange={(e) => setCaudalLitrosSegundo(parseFloat(e.target.value) || 10)}
                        className="w-16 h-6 text-xs bg-zinc-800 border-zinc-600 text-center"
                        data-testid="irrigation-caudal-input"
                      />
                    </div>
                  </div>
                  <div className="text-[10px] text-zinc-500 text-right">
                    <div>1 hora = {M3_POR_HORA(caudalLitrosSegundo).toFixed(1)} m³</div>
                    <div>1 azada = {azadasToHoras(1)} h</div>
                  </div>
                </div>
                
                {/* Row 1: Punto + Horas + Azadas + M³ */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-[10px] uppercase">Punto de Control</Label>
                    <Select value={selectedPoint} onValueChange={setSelectedPoint}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 h-8 text-xs">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {controlPoints.filter(p => p.active).map((point) => (
                          <SelectItem key={point.id} value={point.id} className="text-xs">
                            {point.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-[10px] uppercase">Tiempo</Label>
                    <Input
                      type="number"
                      step="0.25"
                      min="0"
                      value={hoursWater}
                      onChange={(e) => {
                        setHoursWater(e.target.value);
                        if (e.target.value) {
                          setNumAzadas(horasToAzadas(parseFloat(e.target.value)));
                        }
                      }}
                      placeholder="2.5"
                      className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono h-8"
                      data-testid="irrigation-hours-input"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-[10px] uppercase">Nº Azadas</Label>
                    <Input
                      type="number"
                      step="0.25"
                      min="0"
                      value={numAzadas}
                      onChange={(e) => {
                        setNumAzadas(e.target.value);
                        if (e.target.value) {
                          setHoursWater(azadasToHoras(parseFloat(e.target.value)));
                        }
                      }}
                      placeholder="0.5"
                      className="bg-zinc-800 border-amber-700 text-amber-300 font-mono h-8"
                      data-testid="irrigation-azadas-input"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-[10px] uppercase">Metros Cúbicos</Label>
                    <div className="bg-zinc-800 border border-blue-700 rounded px-3 h-8 flex items-center">
                      <span className="text-blue-300 font-mono text-sm">
                        {hoursWater ? calcularM3(parseFloat(hoursWater)) : '0'} m³
                      </span>
                    </div>
                  </div>
                </div>
                  
                {/* Row 2: Selección rápida de horas */}
                <div className="mt-3 sm:mt-4">
                  <Label className="text-zinc-400 text-[10px] sm:text-xs uppercase mb-1.5 sm:mb-2 block font-semibold">Selección Rápida (Horas)</Label>
                  <div className="grid grid-cols-8 gap-1 sm:gap-2">
                    {[0.25, 0.5, 1, 2, 3, 4, 5, 10].map((val) => (
                      <Button
                        key={val}
                        type="button"
                        variant={parseFloat(hoursWater) === val ? "default" : "outline"}
                        onClick={() => {
                          setHoursWater(val.toString());
                          setNumAzadas(horasToAzadas(val));
                        }}
                        className={`h-10 sm:h-12 text-sm sm:text-lg font-bold ${
                          parseFloat(hoursWater) === val 
                            ? 'bg-blue-600 hover:bg-blue-700 border-blue-500 text-white' 
                            : 'border-zinc-600 hover:bg-zinc-700 text-zinc-200 bg-zinc-800'
                        }`}
                      >
                        {val === 0.25 ? '¼' : val === 0.5 ? '½' : val}
                      </Button>
                    ))}
                  </div>
                </div>
                
                {/* Row 3: Fecha + Hora Apertura + Hora Cierre */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-3">
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-[8px] sm:text-[9px] uppercase">Fecha</Label>
                    <Input
                      type="date"
                      value={registrationDate}
                      onChange={(e) => setRegistrationDate(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 sm:h-9 text-[10px] sm:text-xs"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-[8px] sm:text-[9px] uppercase">Apertura</Label>
                    <Input
                      type="time"
                      value={openTime}
                      onChange={(e) => setOpenTime(e.target.value)}
                      className="bg-zinc-800 border-green-600 text-green-300 h-8 sm:h-9 text-[10px] sm:text-xs font-mono"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-[8px] sm:text-[9px] uppercase">Cierre</Label>
                    <Input
                      type="time"
                      value={closeTime}
                      onChange={(e) => setCloseTime(e.target.value)}
                      className="bg-zinc-800 border-red-600 text-red-300 h-8 sm:h-9 text-[10px] sm:text-xs font-mono"
                    />
                  </div>
                </div>
                
                {/* Row 3.5: Desglose Horario - Muestra las horas que está echando agua */}
                {hoursWater && parseFloat(hoursWater) > 0 && openTime && closeTime && (
                  <div className="mt-3 p-3 bg-gradient-to-r from-blue-900/30 to-teal-900/30 rounded-lg border border-blue-500/30">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-[10px] sm:text-xs uppercase text-blue-400 font-bold flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5" />
                        Desglose Horario
                      </h4>
                      <span className="text-[10px] text-zinc-400">
                        1 azada = {M3_POR_AZADA} m³
                      </span>
                    </div>
                    
                    {/* Timeline visual de horas */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(() => {
                        const hours = [];
                        const [openH, openM] = openTime.split(':').map(Number);
                        const [closeH, closeM] = closeTime.split(':').map(Number);
                        let currentH = openH;
                        let currentM = openM;
                        let totalMinutes = parseFloat(hoursWater) * 60;
                        let minutesRemaining = totalMinutes;
                        
                        while (minutesRemaining > 0 && hours.length < 24) {
                          const minutesThisHour = Math.min(60 - currentM, minutesRemaining);
                          const fractionHour = minutesThisHour / 60;
                          const m3ThisHour = fractionHour * M3_POR_HORA(caudalLitrosSegundo);
                          const azadasThisHour = m3ThisHour / M3_POR_AZADA;
                          
                          const endM = currentM + minutesThisHour;
                          const displayEndM = endM >= 60 ? 0 : endM;
                          const displayEndH = endM >= 60 ? (currentH + 1) % 24 : currentH;
                          
                          hours.push({
                            startH: currentH,
                            startM: currentM,
                            endH: displayEndH,
                            endM: displayEndM,
                            fraction: fractionHour,
                            azadas: azadasThisHour,
                            m3: m3ThisHour
                          });
                          
                          minutesRemaining -= minutesThisHour;
                          currentM = 0;
                          currentH = (currentH + 1) % 24;
                        }
                        
                        return hours.map((hour, idx) => (
                          <div 
                            key={idx}
                            className="bg-zinc-800/80 border border-zinc-600 rounded px-2 py-1 text-center min-w-[70px]"
                          >
                            <div className="text-[10px] text-green-400 font-mono">
                              {String(hour.startH).padStart(2, '0')}:{String(hour.startM).padStart(2, '0')}
                              <span className="text-zinc-500"> → </span>
                              <span className="text-red-400">
                                {String(hour.endH).padStart(2, '0')}:{String(hour.endM).padStart(2, '0')}
                              </span>
                            </div>
                            <div className="text-[9px] text-amber-300 font-bold">
                              {hour.azadas.toFixed(2)} azd
                            </div>
                            <div className="text-[8px] text-blue-300">
                              {hour.m3.toFixed(1)} m³
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                    
                    {/* Resumen Total */}
                    <div className="grid grid-cols-3 gap-2 p-2 bg-zinc-900/60 rounded border border-zinc-700">
                      <div className="text-center">
                        <div className="text-[9px] text-zinc-500 uppercase">Total Horas</div>
                        <div className="text-lg font-bold font-mono text-white">
                          {parseFloat(hoursWater).toFixed(2)}
                          <span className="text-xs text-zinc-400 ml-1">h</span>
                        </div>
                        <div className="text-[9px] text-zinc-500">
                          {openTime} - {closeTime}
                        </div>
                      </div>
                      <div className="text-center border-x border-zinc-700">
                        <div className="text-[9px] text-zinc-500 uppercase">Total Azadas</div>
                        <div className="text-lg font-bold font-mono text-amber-400">
                          {horasToAzadas(parseFloat(hoursWater))}
                          <span className="text-xs text-amber-600 ml-1">azd</span>
                        </div>
                        <div className="text-[9px] text-zinc-500">
                          1 azada = {M3_POR_AZADA} m³
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-[9px] text-zinc-500 uppercase">Total m³</div>
                        <div className="text-lg font-bold font-mono text-blue-400">
                          {calcularM3(parseFloat(hoursWater))}
                          <span className="text-xs text-blue-600 ml-1">m³</span>
                        </div>
                        <div className="text-[9px] text-zinc-500">
                          @ {caudalLitrosSegundo} l/s
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Row 4: Operador + Notas */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-2">
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-[8px] sm:text-[9px] uppercase">Operador</Label>
                    <Input
                      value={operator}
                      onChange={(e) => setOperator(e.target.value)}
                      placeholder="Nombre"
                      className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 sm:h-9 text-[10px] sm:text-xs"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-[8px] sm:text-[9px] uppercase">Notas</Label>
                    <Input
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Opcional"
                      className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 sm:h-9 text-[10px] sm:text-xs"
                    />
                  </div>
                </div>
                
                {/* Row 5: Info + Guardar */}
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-zinc-700">
                  {hoursWater && parseFloat(hoursWater) > 0 ? (
                    <span className="text-xs text-blue-400 font-mono">= {calcularM3(parseFloat(hoursWater))} m³ · {horasToAzadas(parseFloat(hoursWater))} azd</span>
                  ) : (
                    <span className="text-[10px] text-zinc-500">1 azada = {M3_POR_AZADA} m³ (12 h a 10 l/s)</span>
                  )}
                  <Button 
                    onClick={handleRegisterConsumption}
                    className="bg-teal-600 hover:bg-teal-700 h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm"
                    disabled={!selectedPoint || !hoursWater}
                  >
                    <Save className="w-3 h-3 mr-1.5" />
                    Guardar Registro
                  </Button>
                </div>
              </Card>
              
              {/* Recent History */}
              <Card className="bg-zinc-800/50 border-zinc-700 p-2">
                <h3 className="font-heading text-[10px] uppercase tracking-wider text-zinc-300 mb-1 flex items-center gap-1">
                  <History className="w-3 h-3" />
                  Últimos Registros
                </h3>
                {history.length === 0 ? (
                  <p className="text-zinc-500 text-[10px]">No hay registros</p>
                ) : (
                  <div className="space-y-0.5 max-h-24 overflow-y-auto">
                    {history.slice(0, 4).map((op) => (
                      <div key={op.id} className="flex items-center justify-between bg-zinc-900/50 rounded px-2 py-1 text-[10px]">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-zinc-300">{op.control_point_name?.split(' ').slice(-1)[0]}</span>
                          <span className="text-zinc-500">{formatDateTime(op.created_at || op.opened_at)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-400 font-mono">{op.hours_water} h</span>
                          <span className="text-amber-400 font-mono font-bold">{op.volume_m3 ? (op.volume_m3 / M3_POR_AZADA).toFixed(2) : horasToAzadas(op.hours_water)} azd</span>
                          <span className="text-cyan-400 font-mono">{op.volume_m3} m³</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>
            
            {/* Consumption Tab */}
            <TabsContent value="consumption" className="space-y-4 mt-4">
              {consumption && (
                <>
                  {/* Summary Cards - GRANDES */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-blue-900/20 border-blue-500/30 p-4 text-center">
                      <span className="text-xs text-blue-400 uppercase font-bold tracking-wider">Hoy</span>
                      <div className="text-3xl font-bold font-mono text-amber-300 mt-2">
                        {(consumption.today.volume_m3 / M3_POR_AZADA).toFixed(2)} <span className="text-lg">azd</span>
                      </div>
                      <div className="text-sm text-zinc-400 mt-1">
                        {consumption.today.hours_water} h · {consumption.today.volume_m3} m³
                      </div>
                    </Card>
                    <Card className="bg-cyan-900/20 border-cyan-500/30 p-4 text-center">
                      <span className="text-xs text-cyan-400 uppercase font-bold tracking-wider">Semana</span>
                      <div className="text-3xl font-bold font-mono text-amber-300 mt-2">
                        {(consumption.week.volume_m3 / M3_POR_AZADA).toFixed(2)} <span className="text-lg">azd</span>
                      </div>
                      <div className="text-sm text-zinc-400 mt-1">
                        {consumption.week.hours_water} h · {consumption.week.volume_m3} m³
                      </div>
                    </Card>
                    <Card className="bg-purple-900/20 border-purple-500/30 p-4 text-center">
                      <span className="text-xs text-purple-400 uppercase font-bold tracking-wider">Mes</span>
                      <div className="text-3xl font-bold font-mono text-amber-300 mt-2">
                        {(consumption.month.volume_m3 / M3_POR_AZADA).toFixed(2)} <span className="text-lg">azd</span>
                      </div>
                      <div className="text-sm text-zinc-400 mt-1">
                        {consumption.month.hours_water} h · {consumption.month.volume_m3} m³
                      </div>
                    </Card>
                  </div>
                  
                  {/* Export/Import row - MÁS GRANDES */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Export */}
                    <Card className="bg-emerald-900/20 border-emerald-500/30 p-4">
                      <h3 className="text-sm uppercase text-emerald-400 mb-3 flex items-center gap-2 font-bold">
                        <Download className="w-4 h-4" />
                        Exportar Excel
                      </h3>
                      <div className="flex gap-2">
                        <Input
                          type="date"
                          value={exportStartDate}
                          onChange={(e) => setExportStartDate(e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-zinc-100 text-sm h-10 flex-1"
                        />
                        <Input
                          type="date"
                          value={exportEndDate}
                          onChange={(e) => setExportEndDate(e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-zinc-100 text-sm h-10 flex-1"
                        />
                        <Button
                          onClick={handleExportExcel}
                          disabled={exporting}
                          className="bg-emerald-600 hover:bg-emerald-700 h-10 px-4"
                        >
                          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        </Button>
                      </div>
                    </Card>
                    
                    {/* Import */}
                    <Card className="bg-blue-900/20 border-blue-500/30 p-4">
                      <h3 className="text-sm uppercase text-blue-400 mb-3 flex items-center gap-2 font-bold">
                        <Upload className="w-4 h-4" />
                        Importar Excel
                      </h3>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleDownloadTemplate}
                          variant="outline"
                          className="flex-1 border-blue-500/30 text-blue-300 hover:bg-blue-900/30 h-10 text-sm"
                        >
                          Plantilla
                        </Button>
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept=".xlsx,.xls"
                          onChange={handleImportExcel}
                          className="hidden"
                          id="excel-import"
                        />
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={importing}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 h-10 text-sm"
                        >
                          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subir"}
                        </Button>
                      </div>
                    </Card>
                  </div>
                  
                  <p className="text-xs text-zinc-500 text-center">1 hora a {caudalLitrosSegundo} l/s = {M3_POR_HORA(caudalLitrosSegundo).toFixed(0)} m³</p>
                </>
              )}
            </TabsContent>
            
            {/* Schedule Tab - Programar tareas para operarios */}
            <TabsContent value="schedule" className="space-y-2 mt-2">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading text-xs uppercase tracking-wider text-zinc-300 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-purple-400" />
                  Programar Tareas
                </h3>
                <Button 
                  size="sm" 
                  onClick={() => setShowScheduleForm(!showScheduleForm)}
                  className="bg-purple-600 hover:bg-purple-700 h-7 text-xs px-3"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Nueva Tarea
                </Button>
              </div>
              
              {/* Formulario nueva tarea */}
              {showScheduleForm && (
                <Card className="bg-purple-900/20 border-purple-800/50 p-3 mb-3">
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[10px] uppercase">Punto de Control</Label>
                      <Select value={newSchedule.point_id} onValueChange={(v) => setNewSchedule({...newSchedule, point_id: v})}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 h-8 text-xs">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {controlPoints.filter(p => p.active).map((point) => (
                            <SelectItem key={point.id} value={point.id} className="text-xs">
                              {point.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[10px] uppercase">Fecha</Label>
                      <Input
                        type="date"
                        value={newSchedule.scheduled_date}
                        onChange={(e) => setNewSchedule({...newSchedule, scheduled_date: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[10px] uppercase">Hora</Label>
                      <Input
                        type="time"
                        value={newSchedule.scheduled_time}
                        onChange={(e) => setNewSchedule({...newSchedule, scheduled_time: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 h-8 text-xs"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-3 mb-3">
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[10px] uppercase">Tiempo</Label>
                      <Input
                        type="number"
                        step="0.25"
                        value={newSchedule.hours_water}
                        onChange={(e) => setNewSchedule({
                          ...newSchedule, 
                          hours_water: e.target.value,
                          azadas: horasToAzadas(parseFloat(e.target.value))
                        })}
                        placeholder="2"
                        className="bg-zinc-800 border-zinc-700 h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[10px] uppercase">Azadas</Label>
                      <Input
                        type="number"
                        step="0.25"
                        value={newSchedule.azadas}
                        onChange={(e) => setNewSchedule({
                          ...newSchedule, 
                          azadas: e.target.value,
                          hours_water: azadasToHoras(parseFloat(e.target.value))
                        })}
                        placeholder="2"
                        className="bg-zinc-800 border-amber-700 h-8 text-xs text-amber-300"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[10px] uppercase">m³ Estimados</Label>
                      <div className="bg-zinc-800 border border-blue-700 rounded px-2 h-8 flex items-center">
                        <span className="text-blue-300 font-mono text-xs">
                          {newSchedule.hours_water ? calcularM3(parseFloat(newSchedule.hours_water)) : '0'} m³
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[10px] uppercase">Operario</Label>
                      <Input
                        value={newSchedule.assigned_operator}
                        onChange={(e) => setNewSchedule({...newSchedule, assigned_operator: e.target.value})}
                        placeholder="Nombre operario"
                        className="bg-zinc-800 border-zinc-700 h-8 text-xs"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Input
                      value={newSchedule.notes}
                      onChange={(e) => setNewSchedule({...newSchedule, notes: e.target.value})}
                      placeholder="Notas o instrucciones para el operario..."
                      className="bg-zinc-800 border-zinc-700 h-8 text-xs flex-1"
                    />
                    <Button onClick={handleScheduleTask} className="bg-purple-600 hover:bg-purple-700 h-8 px-4">
                      <Send className="w-3 h-3 mr-1" />
                      Programar
                    </Button>
                  </div>
                </Card>
              )}
              
              {/* Lista de tareas programadas */}
              <Card className="bg-zinc-800/50 border-zinc-700 p-3">
                <h4 className="text-xs text-zinc-400 uppercase mb-2 flex items-center gap-2">
                  <Bell className="w-3 h-3" />
                  Tareas Pendientes
                </h4>
                {scheduledTasks.filter(t => t.status === 'pending').length === 0 ? (
                  <p className="text-zinc-500 text-xs text-center py-4">No hay tareas programadas</p>
                ) : (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {scheduledTasks.filter(t => t.status === 'pending').map((task) => {
                      const point = controlPoints.find(p => p.id === task.control_point_id);
                      return (
                        <div key={task.id} className="flex items-center justify-between p-2 bg-zinc-900/50 rounded border border-purple-800/30">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-purple-400 font-bold text-xs">{point?.name || 'Punto'}</span>
                              <span className="text-zinc-500 text-[10px]">
                                {task.scheduled_date} {task.scheduled_time}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-amber-300 text-xs font-mono">{task.hours_water}h = {task.azadas || horasToAzadas(task.hours_water)} azadas</span>
                              <span className="text-blue-300 text-xs font-mono">{task.m3_estimated || calcularM3(task.hours_water)} m³</span>
                              {task.assigned_operator && (
                                <span className="text-zinc-400 text-[10px] flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {task.assigned_operator}
                                </span>
                              )}
                            </div>
                            {task.notes && <p className="text-zinc-500 text-[10px] mt-1">{task.notes}</p>}
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-7 w-7 text-green-400 hover:bg-green-900/30"
                              onClick={() => handleCompleteTask(task.id)}
                            >
                              <Save className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-7 w-7 text-red-400 hover:bg-red-900/30"
                              onClick={() => handleDeleteTask(task.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </TabsContent>
            
            {/* Intermediate Points Tab */}
            <TabsContent value="intermediate" className="space-y-2 mt-2">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading text-xs uppercase tracking-wider text-zinc-300 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-amber-400" />
                  Puntos Intermedios
                </h3>
                <Button 
                  size="sm" 
                  onClick={() => setShowAddIntermediate(!showAddIntermediate)}
                  className="bg-amber-600 hover:bg-amber-700 h-7 text-xs px-3"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Nuevo Punto
                </Button>
              </div>
              
              {showAddIntermediate && (
                <Card className="bg-zinc-800/50 border-amber-600/50 p-3 mb-2">
                  <h4 className="text-[10px] uppercase text-amber-400 mb-2 font-bold">Crear Nuevo Punto</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[9px] uppercase">Nombre</Label>
                      <Input
                        value={newIntermediate.name}
                        onChange={(e) => setNewIntermediate({...newIntermediate, name: e.target.value})}
                        placeholder="Ej: Cruce Carretera"
                        className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[9px] uppercase">Presa</Label>
                      <Select 
                        value={newIntermediate.dam_id} 
                        onValueChange={(v) => setNewIntermediate({...newIntermediate, dam_id: v})}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 h-8 text-xs">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="SIBERIO">SIBERIO</SelectItem>
                          <SelectItem value="PARRALILLO">PARRALILLO</SelectItem>
                          <SelectItem value="CAIDERO">CAIDERO</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[9px] uppercase">Distancia (km)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={newIntermediate.distance_to_dam_km}
                        onChange={(e) => setNewIntermediate({...newIntermediate, distance_to_dam_km: e.target.value})}
                        placeholder="0.0"
                        className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[9px] uppercase">Hora Medición</Label>
                      <Input
                        type="time"
                        value={newIntermediate.measurement_time}
                        onChange={(e) => setNewIntermediate({...newIntermediate, measurement_time: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[9px] uppercase">Hora Salida</Label>
                      <Input
                        type="time"
                        value={newIntermediate.departure_time}
                        onChange={(e) => setNewIntermediate({...newIntermediate, departure_time: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs"
                      />
                    </div>
                  </div>
                  <div className="mt-2 space-y-1">
                    <Label className="text-zinc-400 text-[9px] uppercase">Notas</Label>
                    <Input
                      value={newIntermediate.notes}
                      onChange={(e) => setNewIntermediate({...newIntermediate, notes: e.target.value})}
                      placeholder="Observaciones..."
                      className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button 
                      onClick={() => setShowAddIntermediate(false)}
                      variant="outline"
                      className="flex-1 h-8 text-xs border-zinc-600"
                    >
                      Cancelar
                    </Button>
                    <Button 
                      onClick={handleAddIntermediatePoint}
                      className="flex-1 bg-amber-600 hover:bg-amber-700 h-8 text-xs"
                    >
                      <MapPin className="w-3 h-3 mr-1" />
                      Crear
                    </Button>
                  </div>
                </Card>
              )}
              
              {/* Lista de puntos intermedios */}
              <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                {intermediatePoints.length === 0 ? (
                  <div className="text-center py-6 bg-zinc-800/30 rounded-lg border border-zinc-700/50">
                    <MapPin className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                    <p className="text-zinc-500 text-xs">No hay puntos intermedios configurados</p>
                    <p className="text-zinc-600 text-[10px] mt-1">Crea un punto para registrar mediciones</p>
                  </div>
                ) : (
                  intermediatePoints.map((point) => (
                    <Card key={point.id} className="bg-zinc-800/50 border-zinc-700 p-3 overflow-hidden">
                      {/* Header del punto */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <MapPin className="w-4 h-4 text-amber-400 flex-shrink-0" />
                            <span className="font-bold text-zinc-100 text-sm truncate">{point.name}</span>
                            <Badge className="text-[9px] bg-blue-600 flex-shrink-0">{point.dam_id}</Badge>
                          </div>
                          <div className="text-[10px] text-zinc-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                            {point.distance_to_dam_km > 0 && (
                              <span className="whitespace-nowrap">📍 {point.distance_to_dam_km} km</span>
                            )}
                            {point.measurement_time && (
                              <span className="whitespace-nowrap">🕐 Med: {point.measurement_time}</span>
                            )}
                            {point.departure_time && (
                              <span className="whitespace-nowrap">🚗 Sal: {point.departure_time}</span>
                            )}
                          </div>
                          {point.notes && (
                            <div className="text-[9px] text-zinc-500 mt-1 truncate">{point.notes}</div>
                          )}
                        </div>
                        {/* Botones de acción */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <Button 
                            size="sm" 
                            onClick={() => setRegisteringReading(registeringReading === point.id ? null : point.id)}
                            className={`h-7 text-[10px] px-2.5 ${
                              registeringReading === point.id 
                                ? 'bg-amber-600 hover:bg-amber-700' 
                                : 'bg-green-600 hover:bg-green-700'
                            }`}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            {registeringReading === point.id ? 'Cerrar' : 'Medir'}
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost"
                            onClick={() => handleDeleteIntermediatePoint(point.id)}
                            className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Formulario de medición expandible */}
                      {registeringReading === point.id && (
                        <div className="mt-2 pt-2 border-t border-amber-600/30">
                          <h5 className="text-[9px] uppercase text-green-400 mb-1.5 font-bold">Registrar Medición</h5>
                          <div className="grid grid-cols-4 gap-1.5">
                            <div className="space-y-0.5">
                              <Label className="text-zinc-400 text-[8px] uppercase">Nivel (m)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={intermediateReading.level}
                                onChange={(e) => setIntermediateReading({...intermediateReading, level: e.target.value})}
                                placeholder="0.00"
                                className="bg-zinc-800 border-zinc-700 text-zinc-100 h-6 text-[10px]"
                              />
                            </div>
                            <div className="space-y-0.5">
                              <Label className="text-zinc-400 text-[8px] uppercase">Caudal</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={intermediateReading.flow_rate}
                                onChange={(e) => setIntermediateReading({...intermediateReading, flow_rate: e.target.value})}
                                placeholder="l/s"
                                className="bg-zinc-800 border-zinc-700 text-zinc-100 h-6 text-[10px]"
                              />
                            </div>
                            <div className="space-y-0.5">
                              <Label className="text-zinc-400 text-[8px] uppercase">Fecha</Label>
                              <Input
                                type="date"
                                value={intermediateReading.date}
                                onChange={(e) => setIntermediateReading({...intermediateReading, date: e.target.value})}
                                className="bg-zinc-800 border-zinc-700 text-zinc-100 h-6 text-[10px]"
                              />
                            </div>
                            <div className="space-y-0.5">
                              <Label className="text-zinc-400 text-[8px] uppercase">Hora</Label>
                              <Input
                                type="time"
                                value={intermediateReading.time}
                                onChange={(e) => setIntermediateReading({...intermediateReading, time: e.target.value})}
                                className="bg-zinc-800 border-zinc-700 text-zinc-100 h-6 text-[10px]"
                              />
                            </div>
                          </div>
                          <div className="flex gap-1.5 mt-1.5">
                            <Input
                              value={intermediateReading.operator}
                              onChange={(e) => setIntermediateReading({...intermediateReading, operator: e.target.value})}
                              placeholder="Operador"
                              className="bg-zinc-800 border-zinc-700 text-zinc-100 h-6 text-[10px] flex-1"
                            />
                            <Input
                              value={intermediateReading.notes}
                              onChange={(e) => setIntermediateReading({...intermediateReading, notes: e.target.value})}
                              placeholder="Notas"
                              className="bg-zinc-800 border-zinc-700 text-zinc-100 h-6 text-[10px] flex-1"
                            />
                            <Button 
                              onClick={() => handleRegisterIntermediateReading(point.id)}
                              className="bg-green-600 hover:bg-green-700 h-6 text-[10px] px-3"
                            >
                              <Save className="w-3 h-3 mr-1" />
                              Guardar
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))
                )}
              </div>
              
              <p className="text-[9px] text-zinc-500 text-center pt-1 border-t border-zinc-800">
                Los puntos intermedios registran mediciones entre la presa y el destino final
              </p>
            </TabsContent>
            
            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-2 mt-2">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-[10px] uppercase tracking-wider text-zinc-300">
                  Puntos de Control
                </h3>
                <Button 
                  size="sm" 
                  onClick={() => setShowAddPoint(!showAddPoint)}
                  className="bg-blue-600 hover:bg-blue-700 h-6 text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Añadir
                </Button>
              </div>
              
              {showAddPoint && (
                <Card className="bg-zinc-800/50 border-zinc-700 p-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[9px] uppercase">Nombre</Label>
                      <Input
                        value={newPointName}
                        onChange={(e) => setNewPointName(e.target.value)}
                        placeholder="Válvula Norte"
                        className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[9px] uppercase">Ubicación</Label>
                      <Input
                        value={newPointLocation}
                        onChange={(e) => setNewPointLocation(e.target.value)}
                        placeholder="Presa Siberio"
                        className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-[9px] uppercase">Caudal (l/s)</Label>
                      <Input
                        type="number"
                        value={newPointFlowRate}
                        onChange={(e) => setNewPointFlowRate(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={handleAddControlPoint}
                    className="mt-2 bg-emerald-600 hover:bg-emerald-700 h-7 text-xs w-full"
                  >
                    Crear Punto
                  </Button>
                </Card>
              )}
              
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {controlPoints.map((point) => (
                  <Card key={point.id} className="bg-zinc-800/50 border-zinc-700 p-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-bold text-zinc-200 text-xs">{point.name}</span>
                        <div className="text-[10px] text-zinc-400">
                          {point.location} | <span className="text-blue-400 font-mono">{point.flow_rate_ls} l/s</span>
                          <span className="text-amber-400 font-mono ml-1">= 1 azada / {(M3_POR_AZADA / (point.flow_rate_ls * 3.6)).toFixed(0)} h</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge variant={point.active ? "default" : "secondary"} className={`text-[9px] ${point.active ? "bg-emerald-600" : ""}`}>
                          {point.active ? "OK" : "OFF"}
                        </Badge>
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => handleDeleteControlPoint(point.id)}
                          className="h-5 w-5 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IrrigationManager;
